<template>
  <div ref="root" class="relative">
    <button
      type="button"
      class="relative flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
      :aria-label="unread ? `Notifications (${unread} non lues)` : 'Notifications'"
      aria-haspopup="true"
      :aria-expanded="open"
      @click="toggle"
    >
      <AppIcon name="bell" />
      <span v-if="unread" class="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-coral px-1 text-[10px] font-bold text-white">
        {{ unread > 99 ? '99+' : unread }}
      </span>
    </button>

    <Transition name="pop">
      <div v-if="open" class="absolute right-0 z-50 mt-2 w-[min(92vw,24rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-700">
          <h2 class="text-sm font-bold text-slate-900 dark:text-slate-100">Notifications</h2>
          <button v-if="unread" type="button" class="text-xs font-semibold text-cyan-700 hover:underline dark:text-cyan-300" @click="readAll">Tout marquer comme lu</button>
        </div>
        <div class="max-h-96 overflow-y-auto">
          <div v-if="loading && !items.length" class="p-6 text-center text-sm text-slate-400">Chargement…</div>
          <p v-else-if="error" class="p-6 text-center text-sm text-rose-600 dark:text-rose-400">{{ error }}</p>
          <p v-else-if="!items.length" class="p-6 text-center text-sm text-slate-400">Aucune notification.</p>
          <button
            v-for="n in items"
            :key="n.id"
            type="button"
            class="flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-700/60 dark:hover:bg-slate-700/50"
            @click="openNotification(n)"
          >
            <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full" :class="n.read_at ? 'bg-transparent' : 'bg-cyan-500'" />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{{ n.title }}</span>
              <span class="line-clamp-2 block text-xs text-slate-500 dark:text-slate-400">{{ n.message }}</span>
              <span class="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">{{ $session.dateTime(n.created_at) }}</span>
            </span>
          </button>
        </div>
        <router-link to="/notifications" class="block border-t border-slate-100 px-4 py-3 text-center text-sm font-semibold text-cyan-700 hover:bg-slate-50 dark:border-slate-700 dark:text-cyan-300 dark:hover:bg-slate-700/50" @click="open = false">
          Voir toutes les notifications
        </router-link>
      </div>
    </Transition>
  </div>
</template>

<script>
import AppIcon from '@/components/ui/AppIcon.vue'
import { api } from '@/api'
import { notificationTarget } from '@/utils/notifications'
import { subscribeRealtime } from '@/stores/realtime'
import { toast } from '@/stores/toast'

// Repli permanent : le compteur est interrogé toutes les 45 s (30 à 60 s
// recommandées) même quand le temps réel est actif, car celui-ci peut être
// désactivé ou indisponible. La liste n'est chargée qu'à l'ouverture du panneau.
const POLL_MS = 45000

export default {
  name: 'NotificationBell',
  components: { AppIcon },
  data() {
    return { unread: 0, open: false, items: [], loading: false, error: '', timer: null, seen: null }
  },
  mounted() {
    this.refreshCount()
    this.syncNotifications() // mémorise les non lues existantes : elles ne seront pas annoncées
    this.timer = setInterval(() => {
      if (!document.hidden) this.refreshCount()
    }, POLL_MS)
    document.addEventListener('click', this.onOutside)
    document.addEventListener('keydown', this.onKey)
    // Le temps réel accélère la mise à jour ; l'interrogation ci-dessus reste le repli.
    const refresh = () => {
      this.refreshCount()
      this.syncNotifications({ announce: true })
      if (this.open) this.loadList()
    }
    this.stopRealtime = subscribeRealtime({ onNotification: refresh, onReconnect: refresh })
  },
  beforeUnmount() {
    if (this.stopRealtime) this.stopRealtime()
    clearInterval(this.timer)
    document.removeEventListener('click', this.onOutside)
    document.removeEventListener('keydown', this.onKey)
  },
  methods: {
    async refreshCount() {
      try {
        this.unread = await api.notifications.unreadCount()
      } catch (e) {
        /* le compteur est accessoire : on ignore les erreurs ponctuelles */
      }
    },
    /**
     * Repère les nouvelles notifications non lues et les annonce par une alerte. Le signal temps réel
     * ne contient que des identifiants : le texte affiché vient de l'API (GET /notifications).
     */
    async syncNotifications({ announce = false } = {}) {
      try {
        const res = await api.notifications.list({ per_page: 20, unread: '1' })
        const fresh = res.data.filter((n) => !(this.seen && this.seen.has(n.id)))
        const firstSync = this.seen === null
        this.seen = new Set([...(this.seen || []), ...res.data.map((n) => n.id)])
        if (!announce || firstSync || !fresh.length) return
        if (fresh.length > 3) toast.info(`${fresh.length} nouvelles notifications`, 8000)
        else fresh.forEach((n) => toast.info(`${n.title} — ${n.message}`, 8000))
      } catch (e) {
        /* accessoire : le compteur reste la référence */
      }
    },
    async toggle() {
      this.open = !this.open
      if (this.open) await this.loadList()
    },
    async loadList() {
      this.loading = true
      this.error = ''
      try {
        const res = await api.notifications.list({ per_page: 10 })
        this.items = res.data
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    async openNotification(n) {
      this.open = false
      if (!n.read_at) {
        try {
          await api.notifications.markRead(n.id)
          n.read_at = new Date().toISOString()
          this.unread = Math.max(0, this.unread - 1)
        } catch (e) {
          /* la navigation prime sur le marquage */
        }
      }
      this.$router.push(notificationTarget(n))
    },
    async readAll() {
      try {
        await api.notifications.markAllRead()
        this.items.forEach((n) => (n.read_at = n.read_at || new Date().toISOString()))
        this.unread = 0
      } catch (e) {
        this.error = e.message
      }
    },
    onOutside(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) this.open = false
    },
    onKey(event) {
      if (event.key === 'Escape') this.open = false
    }
  }
}
</script>
